import { Button } from '@/components/ui/button';
import { ICS204_MUTED } from './assignmentCardStyles';
import { Ics204CardSection } from './ics204BoxLabels';
import {
  formatSignatureDateTime,
  ICS204_OSC_REVIEW_ROLE,
  ICS204_PREPARED_BY_SIGN_ROLE,
  ICS204_PSC_REVIEW_ROLE,
  ICS204_REQUIRED_SIGNATURE_SLOTS,
  type WalaSignature,
} from './ics204SignatureSlots';

type Ics204SignatureBoxesProps = {
  signatures: WalaSignature[];
  /**
   * Show review buttons when Prepared By exists and not creating a private
   * draft.
   */
  showReviewActions: boolean;
  disabled?: boolean;
  onReviewAs: (
    role: typeof ICS204_PSC_REVIEW_ROLE | typeof ICS204_OSC_REVIEW_ROLE
  ) => void;
};

const FilledSignature = ({
  signature,
}: {
  signature: WalaSignature | undefined;
}) => {
  if (!signature) {
    return <div className={ICS204_MUTED}>Not signed</div>;
  }
  return (
    <div className="tw:grid tw:gap-1 tw:text-sm">
      <div>
        <span className="tw:text-muted-foreground">Name: </span>
        {signature.name}
      </div>
      <div>
        <span className="tw:text-muted-foreground">Position / Title: </span>
        {signature.role}
      </div>
      <div className="tw:italic">
        <span className="tw:not-italic tw:text-muted-foreground">
          Signature:{' '}
        </span>
        {signature.name}
      </div>
      <div>
        <span className="tw:text-muted-foreground">Date / Time: </span>
        {formatSignatureDateTime(signature.signedAt)}
      </div>
    </div>
  );
};

const Ics204SignatureBoxes = ({
  signatures,
  showReviewActions,
  disabled = false,
  onReviewAs,
}: Ics204SignatureBoxesProps) => {
  const hasPreparedBy =
    signatures.length > 0 &&
    signatures[0]?.role === ICS204_PREPARED_BY_SIGN_ROLE;

  return (
    <div
      className="tw:mb-3 tw:grid tw:gap-3"
      data-testid="ics204-signature-boxes"
    >
      {ICS204_REQUIRED_SIGNATURE_SLOTS.map((slot) => {
        const signature = slot.findSignature(signatures);
        const isReviewSlot =
          slot.role === ICS204_PSC_REVIEW_ROLE ||
          slot.role === ICS204_OSC_REVIEW_ROLE;
        const canReview =
          showReviewActions &&
          hasPreparedBy &&
          isReviewSlot &&
          !slot.isComplete(signatures);

        return (
          <Ics204CardSection
            key={slot.id}
            boxLabel={slot.boxLabel}
            testId={`ics204-section-signature-${slot.id}`}
            className="tw:mt-0"
          >
            {signature ? (
              <FilledSignature signature={signature} />
            ) : canReview ? (
              <Button
                type="button"
                variant="outline"
                className="tw:w-full tw:border-dashed"
                data-testid={`ics204-review-as-${slot.id}`}
                disabled={disabled}
                onClick={() =>
                  onReviewAs(
                    slot.role as
                      | typeof ICS204_PSC_REVIEW_ROLE
                      | typeof ICS204_OSC_REVIEW_ROLE
                  )
                }
              >
                Review as {slot.label}
              </Button>
            ) : (
              <div className={ICS204_MUTED}>Awaiting signature</div>
            )}
          </Ics204CardSection>
        );
      })}
    </div>
  );
};

export default Ics204SignatureBoxes;
